import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cn } from '../../lib/cn'
import { Spinner } from './Spinner'
import { CONTROL_H } from './Field'

type Variant = 'primary' | 'strong' | 'secondary' | 'ghost' | 'danger' | 'outline'
type Size = 'sm' | 'md' | 'lg' | 'icon' | 'icon-sm' | 'icon-xs'

const variants: Record<Variant, string> = {
  primary: 'bg-ginger-500 text-white hover:bg-ginger-400 active:bg-ginger-600',
  strong: 'bg-ink-100 text-canvas hover:bg-ink-200 active:bg-ink-300',
  secondary: 'border border-cream-300 bg-cream-50 text-ink-100 hover:bg-cream-200 active:bg-cream-300',
  outline: 'border border-cream-300 bg-cream-50 text-ink-100 hover:border-ginger-500 hover:text-ginger-600',
  ghost: 'bg-transparent text-ink-300 hover:bg-cream-200 hover:text-ink-100',
  danger: 'border border-cream-300 bg-cream-50 text-red-600 hover:bg-red-50 hover:border-red-300 active:bg-red-100'
}
const sizes: Record<Size, string> = {
  sm: `${CONTROL_H.sm} px-2.5 text-[12.5px] rounded-[7px] gap-1.5 max-sm:text-[13px]`,
  md: `${CONTROL_H.md} px-3.5 text-[13.5px] rounded-lg gap-2 max-sm:text-sm`,
  lg: 'h-11 px-5 text-sm rounded-lg gap-2',
  icon: 'h-9 w-9 rounded-lg max-sm:h-11 max-sm:w-11',
  'icon-sm': 'h-[30px] w-[30px] rounded-[7px] max-sm:h-9 max-sm:w-9',
  'icon-xs': 'h-6 w-6 rounded-md p-0'
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  loading?: boolean
}

/** Buttons: one primary per area; danger always behind a confirmation; ≥44px targets on touch via `lg`. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', loading, children, disabled, type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cn(
        'inline-flex shrink-0 items-center justify-center font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        variants[variant], sizes[size], className
      )}
      {...props}
    >
      {loading && <Spinner className="h-4 w-4" />}
      {children}
    </button>
  )
)
Button.displayName = 'Button'
